<template>
  <div class="wishlist-product-list" v-if="listContents.length > 0">
    <ProductCard
      v-for="product in listContents"
      :key="product.epi"
      type="TypeD"
      :product="product"
      :available="productAvailability[product.epi]?.available"
      @product-click="handleProductClick"
      @remove-from-wishlist="handleRemoveFromWishlist"
      @add-to-cart="handleAddToCart"
    />
  </div>

  <div v-else class="wishlist-empty-content">
    <p class="wishlist-empty-content__text">It looks like your wishlist is empty at the moment!</p>
    <button
      class="btn btn--secondary btn--medium wishlist-empty-content__continue-shopping"
      @click="handleContinueShopping"
    >
      CONTINUE SHOPPING
    </button>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useStore } from 'vuex';
  import { deleteMultipleProductsFromList, deleteProductFromList } from '../../../api';
  import ProductCard from '../../../../global/product-card/index.vue';

  const store = useStore();

  const selectedWishlistDetails = computed(() => store.state.selectedWishlistDetails);
  const listContents = computed(() => selectedWishlistDetails.value.listcontents);
  const productAvailability = ref({});

  // Call on component mount
  onMounted(() => {
    checkProductsAvailability();
  });

  // Function to check product availability
  const checkProductsAvailability = async () => {
    if (!listContents.value || listContents.value.length === 0) return;

    try {
      // Create promises for all products
      const availabilityPromises = listContents.value.map(async (listItem) => {
        try {
          const productHandle = listItem.cprops?.productDetails?.handle;
          const response = await fetch(window.location.origin + `/products/${productHandle}.js`);
          const productData = await response.json();
          
          // Check if the selected variant ID exists in the product's variants
          const selectedVariantId = listItem.epi; // epi is the variant ID
          const variantExists = productData.variants.find(variant => variant.id == selectedVariantId);

          return {
            epi: listItem.epi,
            available: variantExists?.available,
            variantExists: variantExists,
            productData,
            listItem
          };
        } catch (error) {
          console.error(`Error fetching product ${listItem.epi}:`, error);
          return {
            epi: listItem.epi,
            available: false,
            variantExists: false,
            error,
            listItem
          };
        }
      });

      // Wait for all promises to resolve
      const results = await Promise.all(availabilityPromises);
            
      const unavailableVariants = [];

      // Update availability state
      const availabilityMap = {};
      results.forEach(result => {
        availabilityMap[result.epi] = {
          available: result.available,
          variantExists: result.variantExists,
          productData: result.productData,
          error: result.error
        };

        // If variant doesn't exist in the product, add to unavailable list
        if (!result.variantExists) {
          unavailableVariants.push({
            epi: result.epi,
            du: result.listItem.du,
            empi: result.listItem.empi
          });
        }
      });
      
      productAvailability.value = availabilityMap;
      
      // Automatically remove unavailable variants
      if (unavailableVariants.length > 0) {        
        try {
          await deleteMultipleProductsFromList(selectedWishlistDetails.value.lid, unavailableVariants);
          
          // Filter out the removed items from listcontents
          const unavailableEpis = unavailableVariants.map(item => item.epi);
          const updatedListContents = selectedWishlistDetails.value.listcontents.filter(
            (item) => !unavailableEpis.includes(item.epi)
          );
          
          const updatedSelectedWishlist = {
            ...selectedWishlistDetails.value,
            listcontents: updatedListContents
          };
          
          // Update store with new data
          store.dispatch('setSelectedWishlistDetails', updatedSelectedWishlist);
          
          // Fetch latest wishlist items to ensure sync
          store.dispatch('fetchWishlistItems');
        } catch (deleteError) {
          console.error('Error deleting unavailable variants:', deleteError);
        }
      }
    } catch (error) {
      console.error('Error checking products availability:', error);
    }
  };

  const handleRemoveFromWishlist = async (product) => {
    try {
      const { lid } = selectedWishlistDetails.value;
      const { du, epi, empi } = product;

      const response = await deleteProductFromList(lid, du, empi, epi);

      if (response) {
        const updatedSelectedWishlist = {
          ...selectedWishlistDetails.value,
          listcontents: selectedWishlistDetails.value.listcontents.filter((item) => item.epi != epi),
        };
        store.dispatch('setSelectedWishlistDetails', updatedSelectedWishlist);
        store.dispatch('fetchWishlistItems');
      }
    } catch (error) {
      console.error('Error!', error);
    }
  };

  const handleAddToCart = async (product) => {
    window.openCart({ cartConfirmation: true });
    await window.Additem({ id: product.epi, quantity: 1 }, { visibility: false });
  };

  const handleContinueShopping = () => {
    window.location.href = '/';
  };

  const handleProductClick = (product) => {
    const { cprops } = product;

    // Ensure product details and variant details exist
    const productHandle = cprops?.productDetails?.handle;
    const selectedColor = cprops?.variantDetails?.Color || '';

    window.location.href = `/products/${productHandle}/?color=${selectedColor}`;
  };
</script>
